import { Capacitor } from '@capacitor/core';
import api from './api';
import { startAlert, unlockAudio } from './chime';

// Which app this build is: 'customer' | 'driver' | null (web with all 4 portals).
export const TARGET_APP = process.env.REACT_APP_TARGET_APP || null;
export const isNative = () => Capacitor.isNativePlatform();
export const nativePlatform = () => Capacitor.getPlatform();

let registered = null;

export const registerPush = async ({ role, userId }) => {
  if (!isNative() || !userId) return null;
  const key = `${role}:${userId}`;
  if (registered === key) return null;
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    let permission = await PushNotifications.checkPermissions();
    if (permission.receive !== 'granted') permission = await PushNotifications.requestPermissions();
    if (permission.receive !== 'granted') return null;
    registered = key;
    PushNotifications.removeAllListeners();
    PushNotifications.addListener('registration', (token) => {
      api.registerDevice({ token: token.value, platform: nativePlatform(), role, userId, app: TARGET_APP }).catch(() => {});
    });
    PushNotifications.addListener('registrationError', () => { registered = null; });
    PushNotifications.addListener('pushNotificationReceived', async () => { await unlockAudio(); startAlert(4); });
    await PushNotifications.register();
    return true;
  } catch (e) {
    registered = null;
    return null;
  }
};

export const prepareNativeShell = async () => {
  if (!isNative()) return;
  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    await StatusBar.setOverlaysWebView({ overlay: false });
    await StatusBar.setStyle({ style: TARGET_APP === 'driver' ? Style.Light : Style.Dark });
    if (nativePlatform() === 'android') {
      await StatusBar.setBackgroundColor({ color: TARGET_APP === 'driver' ? '#FFFFFF' : '#EE3B36' });
      // The Android WebView is already fitted between the status bar and the
      // navigation bar, so the CSS safe-area insets must not be added again.
      document.documentElement.classList.add('native-fitted');
    }
  } catch { /* status bar plugin unavailable on web */ }
  try {
    const { SplashScreen } = await import('@capacitor/splash-screen');
    await SplashScreen.hide();
  } catch { /* noop */ }
};

// ---------------------------------------------------------------- native camera
// Returns a File taken with the device camera / photo library, or null on the web
// so the caller can fall back to its hidden <input type="file">.
export const nativePhoto = async (from = 'camera') => {
  if (!isNative()) return null;
  try {
    const { Camera, CameraResultType, CameraSource } = await import('@capacitor/camera');
    const shot = await Camera.getPhoto({
      quality: 85,
      allowEditing: false,
      correctOrientation: true,
      saveToGallery: false,
      resultType: CameraResultType.Uri,
      source: from === 'camera' ? CameraSource.Camera : CameraSource.Photos,
    });
    const blob = await (await fetch(shot.webPath)).blob();
    const type = blob.type || `image/${shot.format === 'png' ? 'png' : 'jpeg'}`;
    return new File([blob], `foto-${Date.now()}.${type.includes('png') ? 'png' : 'jpg'}`, { type });
  } catch {
    return null; // cancelled by the user or permission denied
  }
};

// ---------------------------------------------------------------- contacts
// Lets the customer pick a recipient straight from the phone book.
export const pickContactPhone = async () => {
  if (!isNative()) return null;
  try {
    const { Contacts } = await import('@capacitor-community/contacts');
    const { contact } = await Contacts.pickContact({ projection: { name: true, phones: true } });
    if (!contact) return null;
    const phone = (contact.phones || []).map((p) => p.number).find(Boolean) || '';
    return { name: contact.name?.display || '', phone: phone.replace(/[^\d+]/g, '') };
  } catch {
    return null;
  }
};

// ---------------------------------------------------------------- background GPS
let watcherId = null;

export const startBackgroundTracking = async (onLocation) => {
  if (!isNative() || watcherId) return false;
  try {
    const { registerPlugin } = await import('@capacitor/core');
    const BackgroundGeolocation = registerPlugin('BackgroundGeolocation');
    watcherId = await BackgroundGeolocation.addWatcher({
      backgroundTitle: 'Entrega en curso',
      backgroundMessage: 'Delibery 7 comparte tu ubicación con el cliente mientras entregas.',
      requestPermissions: true,
      stale: false,
      distanceFilter: 20,
    }, (location, error) => {
      if (error || !location) return;
      onLocation({ lat: location.latitude, lng: location.longitude, accuracy: location.accuracy });
    });
    return true;
  } catch {
    watcherId = null;
    return false;
  }
};

export const stopBackgroundTracking = async () => {
  if (!watcherId) return;
  const id = watcherId;
  watcherId = null;
  try {
    const { registerPlugin } = await import('@capacitor/core');
    await registerPlugin('BackgroundGeolocation').removeWatcher({ id });
  } catch { /* already gone */ }
};

// ---------------------------------------------------------------- haptics / share / lifecycle
export const haptic = async (style = 'medium') => {
  if (!isNative()) return;
  try {
    const { Haptics, ImpactStyle, NotificationType } = await import('@capacitor/haptics');
    if (style === 'success') return Haptics.notification({ type: NotificationType.Success });
    if (style === 'error') return Haptics.notification({ type: NotificationType.Error });
    return Haptics.impact({ importance: ImpactStyle[style === 'light' ? 'Light' : 'Medium'], style: ImpactStyle[style === 'light' ? 'Light' : 'Medium'] });
  } catch { /* noop */ }
};

export const shareNative = async ({ title, text, url }) => {
  if (!isNative()) return false;
  try {
    const { Share } = await import('@capacitor/share');
    await Share.share({ title, text, url, dialogTitle: title });
    return true;
  } catch {
    return false;
  }
};

export const onAppResume = (cb) => {
  if (!isNative()) return () => {};
  let handle = null;
  import('@capacitor/app').then(({ App }) => App.addListener('resume', cb).then((h) => { handle = h; })).catch(() => {});
  return () => { handle?.remove?.(); };
};
